import { useState } from "react";
import {
  createManufacturingVendor,
  type MfgVendor,
} from "@/lib/manufacturing/purchaseOrderClient";
import { VendorExtraContactsEditor } from "@/components/manufacturing/VendorExtraContactsEditor";
import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { emptyVendorExtraContact, type MfgVendorExtraContact } from "@/lib/manufacturing/vendorExtraContacts";

type Draft = {
  name: string;
  primary_contact_name: string;
  phone: string;
  email: string;
  address: string;
  extra_contacts: MfgVendorExtraContact[];
};

const BLANK: Draft = {
  name: "",
  primary_contact_name: "",
  phone: "",
  email: "",
  address: "",
  extra_contacts: [emptyVendorExtraContact()],
};

export function GeneratePurchaseOrderNewVendor(props: {
  enabled: boolean;
  busy: boolean;
  onCreated: (vendor: MfgVendor) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const locked = !props.enabled || props.busy || saving;

  async function submit() {
    const name = draft.name.trim();
    if (!name) {
      setError("Vendor name is required.");
      return;
    }
    setSaving(true);
    setError(null);
    const res = await createManufacturingVendor({
      name,
      primary_contact_name: draft.primary_contact_name,
      phone: draft.phone,
      email: draft.email,
      address: draft.address,
      extra_contacts: draft.extra_contacts,
    });
    setSaving(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    props.onCreated(res.data.vendor);
  }

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 dark:border-indigo-900 dark:bg-indigo-950/20">
      <div className="text-xs font-semibold text-gray-800 dark:text-zinc-100">New vendor</div>
      <p className="mb-2 text-[11px] text-gray-500">
        Added to this org&apos;s vendor directory and selected on this PO.
      </p>
      {error ? (
        <div className="mb-2 rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-900/30 dark:text-rose-100">
          {error}
        </div>
      ) : null}
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="text-xs sm:col-span-2">
          <span className="font-semibold">Name *</span>
          <input
            className={`${inputClass} mt-1 w-full`}
            value={draft.name}
            disabled={locked}
            autoFocus
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          />
        </label>
        <label className="text-xs sm:col-span-2">
          <span className="font-semibold">Primary contact</span>
          <input
            className={`${inputClass} mt-1 w-full`}
            value={draft.primary_contact_name}
            disabled={locked}
            placeholder="Name of main contact"
            onChange={(e) =>
              setDraft((d) => ({ ...d, primary_contact_name: e.target.value }))
            }
          />
        </label>
        <label className="text-xs">
          <span className="font-semibold">Phone</span>
          <input
            className={`${inputClass} mt-1 w-full`}
            value={draft.phone}
            disabled={locked}
            onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
          />
        </label>
        <label className="text-xs">
          <span className="font-semibold">Email</span>
          <input
            className={`${inputClass} mt-1 w-full`}
            value={draft.email}
            disabled={locked}
            onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
          />
        </label>
        <label className="text-xs sm:col-span-2">
          <span className="font-semibold">Address</span>
          <textarea
            className={`${inputClass} mt-1 w-full`}
            rows={2}
            value={draft.address}
            disabled={locked}
            onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))}
          />
        </label>
        <VendorExtraContactsEditor
          contacts={draft.extra_contacts}
          onChange={(extra_contacts) => setDraft((d) => ({ ...d, extra_contacts }))}
          disabled={locked}
        />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          disabled={locked}
          onClick={() => void submit()}
        >
          {saving ? "Saving…" : "Create vendor"}
        </button>
        <button type="button" className={ghostBtnClass} disabled={saving} onClick={props.onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
